import React from "react";
import { graphql } from "gatsby";
import parse from 'html-react-parser';
import PostThumbnail from "@/types/PostPreview";
import { RiverGrid, RiverPageContext } from "@/components/RiverGrid";
import { Seo } from "@/components/Seo";
import { RoutePaths } from "@/helpers";
import { Title } from "@/components/RiverGrid/Header";
import { getThumbnails } from "@/helpers/river";
import { emConfig as config } from "@/emConfiguration";
import SeoDetails from "@/types/SEO";
import { trimTrailingSlash } from '@emarketer/code-helpers';
import { LearningCenterLayout } from '@/components/Layout/LearningCenterLayout';


const LearningCenterResourcesArchive = ({ data, pageContext: { currentPage, totalPages } }: {
  data: {
    allWpPage: {
      nodes: PostThumbnail[]
    }
  }
  pageContext: RiverPageContext
}): JSX.Element => {
  const pages = data.allWpPage.nodes;
  const subTitle = parse(`Browse recent Resources exploring the many ways digital 
    transformation is changing your industry, curated by the EMARKETER team.`);

  const thumbnails = getThumbnails(pages, RoutePaths.rivers.category.root);


  const pageTitle = 'Resources';

  return (
    <LearningCenterLayout>
      <div className="min-h-resources-placeholder" />
      <Title title={pageTitle} subTitle={subTitle} hasWiderTitle={false} />
      <RiverGrid
        noData={!pages.length ? "No Resources." : null }
        currentPage={currentPage} 
        totalPages={totalPages}
        rootPath={RoutePaths.rivers.learningcenter.resources.root}
        extensionPath={RoutePaths.rivers.learningcenter.resources.extension}
      >
        {thumbnails}
      </RiverGrid>
      <div className="min-h-resources-placeholder" />
    </LearningCenterLayout>
  );
};

export default LearningCenterResourcesArchive;

// eslint-disable-next-line react/no-multi-comp
export const Head = (): JSX.Element => {
  const subTitle = parse(`Browse recent Resources exploring the many ways digital 
    transformation is changing your industry, curated by the EMARKETER team.`);

  const pageTitle = 'Resources';
  const seoSchema = {
    '@context': 'https://schema.org',
    '@graph': [{
      '@type': 'WebPage',
      headline: pageTitle,
      name: pageTitle,
      description: `${subTitle}`,
      url: `${trimTrailingSlash(config.iiHomeUrl)}/learningcenter/resources`,
      image: {
        '@type': 'ImageObject',
        url: config.commonDefaultSocialThumbnailUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'EMARKETER',
        logo: {
          '@type': 'ImageObject',
          url: config.commonLdJsonLogoUrl,
        },
      },
    }]
  };

  const seo: SeoDetails = {
    schema: {
      raw: JSON.stringify(seoSchema)
    }
  };

  const dataLayerSectionDetails = { 
    unifiedAssetId: `learningcenter-resources`,
    section: 'learningcenter-resources'
  };

  return (
    <Seo
      title={pageTitle} seo={seo} skipParsingSchema dataLayerDetails={dataLayerSectionDetails}
    />
  );
};

export const pageQuery = graphql`
  query LearningCenterResourcesArchive($offset: Int!, $postsPerPage: Int!, $parentDatabaseId: Int!) {
    allWpPage(
      sort: { date: DESC }
      limit: $postsPerPage
      skip: $offset
      filter: { parentDatabaseId:  { eq: $parentDatabaseId }  }
    ) {
      nodes {
        id
        uri
        title
        featuredImage {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED, width: 1000, quality: 100)
              }
            }
          }
        }
        categories {
          nodes {
            name
          }
        }
      }
    }
  }
`;